import {inject, Injectable} from '@angular/core';
import {RegionService} from '../region.service';
import {StorageService} from '../storage.service';
import {type V4Response} from './api-base.type';
import {toV4ApiBase, V4HttpClient} from './v4-http.client';

export interface PublicAccountInfo {
  id?: string;
  username?: string;
  nickname?: string | null;
  [key: string]: unknown;
}

export interface AccountSession {
  id: string;
  aal: string;
  created_at: string;
  updated_at: string;
  refreshed_at: string | null;
  factor_id: string | null;
  not_after: string | null;
  user_agent: string | null;
  ip: string | null;
  current: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AccountApiService {
  private readonly v4Http = inject(V4HttpClient);
  private readonly regionService = inject(RegionService);
  private readonly storageService = inject(StorageService);

  private clientId: string | null = null;

  private get baseUrl(): string {
    return toV4ApiBase(this.regionService.env.apiPrefix.base || '') + '/account';
  }

  private post<T>(
    path: string,
    body: unknown,
    options?: {silentError?: boolean; headers?: Record<string, string>},
  ): Promise<V4Response<T>> {
    return this.v4Http.post<T>(`${this.baseUrl}${path}`, body, options);
  }

  private async getClientId(): Promise<string | null> {
    this.clientId ??= await this.storageService.lfGet('clientId') as string | null;
    return this.clientId;
  }

  /**
   * Current-user global Auth profile (Supabase). Does not require regional projection.
   */
  async getGlobalInfo(params: Record<string, unknown> = {}): Promise<V4Response<Record<string, unknown>>> {
    return this.post('/global-info', params, {silentError: true});
  }

  async getUserInfo(
    params: {user_id: string},
    options?: {silentError?: boolean},
  ): Promise<V4Response<PublicAccountInfo>> {
    return this.post('/info', params, options);
  }

  /**
   * Ensure the current regional account projection exists (upsert from Supabase Auth).
   * Call after Supabase sign-in. Uses silent HTTP errors so callers own the toast.
   */
  async ensureProjection(params: {user_id?: string} = {}): Promise<V4Response<PublicAccountInfo>> {
    return this.post('/ensure-projection', params, {silentError: true});
  }

  async getCaptcha(params: {username: string; action: string}): Promise<V4Response<null>> {
    const clientId = await this.getClientId();
    return this.post('/get-captcha', {
      username: params.username,
      action: params.action,
      ...(clientId ? {client_id: clientId} : {}),
    });
  }

  async updateNickname(params: {user_id: string; nickname: string}): Promise<V4Response<null>> {
    return this.post('/update/nickname', params);
  }

  async deleteAccount(
    params: {user_id: string},
    verificationTicket: string,
  ): Promise<V4Response<null>> {
    return this.post('/delete', params, {
      headers: {'X-Security-Verification': verificationTicket},
    });
  }

  async getSessions(): Promise<V4Response<AccountSession[]>> {
    return this.post('/sessions/list', {});
  }

  async revokeSession(sessionId: string): Promise<V4Response<null>> {
    return this.post('/sessions/revoke', {session_id: sessionId});
  }

  async issueSecurityVerification(params: {
    action: string;
    resource: Record<string, unknown>;
    method: 'email_otp' | 'mfa';
    code?: string;
  }): Promise<V4Response<{ticket: string; expires_at: string}>> {
    return this.post('/security-verification/issue', params);
  }

  async authorizeSecurityAddition(
    type: 'mfa' | 'passkey',
    params: {user_id: string},
    ticket: string,
  ): Promise<V4Response<null>> {
    return this.post(`/${type}/add/authorize`, params, {
      headers: {'X-Security-Verification': ticket},
    });
  }

  async removeMfaFactor(
    params: {user_id: string; factor_id: string},
    ticket: string,
  ): Promise<V4Response<null>> {
    return this.post('/mfa/remove', params, {
      headers: {'X-Security-Verification': ticket},
    });
  }

  async removePasskey(
    params: {user_id: string; passkey_id: string},
    ticket: string,
  ): Promise<V4Response<null>> {
    return this.post('/passkey/remove', params, {
      headers: {'X-Security-Verification': ticket},
    });
  }
}
